import { ScrollArea } from '@neo/primitives/scroll-area'
import { Typography } from '@neo/primitives/typography'
import type { RExternalApp, RExternalAppDetails } from 'v2/types/api/settings'
import { resolveAppDescription } from './app-descriptions'
import { AppLogo, CatalogRow } from './catalog-row'

interface IntegrationAppSelectionProps {
  externalApps: RExternalApp
  searchTerm: string
  activeCategories: string[]
  onAppClick: (app: RExternalAppDetails) => void
}

const AppRow = ({ app, onClick }: { app: RExternalAppDetails; onClick: () => void }) => (
  <CatalogRow
    icon={<AppLogo app={app} />}
    title={app.name}
    description={resolveAppDescription(app)}
    onClick={onClick}
    testId={`button-app-${app.name.toLowerCase().replace(/\s/g, '-')}`}
  />
)

export const IntegrationAppSelection = ({
  externalApps,
  searchTerm,
  activeCategories,
  onAppClick,
}: IntegrationAppSelectionProps) => {
  const normalizedSearch = searchTerm.toLowerCase()
  const categories = Object.entries(externalApps).flatMap(([name, apps]) => {
    if (activeCategories.length > 0 && !activeCategories.includes(name)) return []
    const matching = ((apps ?? []) as RExternalAppDetails[]).filter((app) =>
      app.name.toLowerCase().includes(normalizedSearch),
    )
    return matching.length > 0 ? [{ name, apps: matching }] : []
  })

  const hasNoMatches = categories.length === 0

  return (
    // `hideScrollbar` leaves no bar for the rows to dodge, so the list needs no right gutter.
    <ScrollArea hideScrollbar className="h-full w-full">
      <div className="mt-3 space-y-5 pb-4">
        {categories.map((category) => (
          <div key={category.name}>
            {/* Always shown: with the filter behind an icon, headings are the only on-screen
                record of which categories are showing. */}
            <Typography variant="XS/500" className="mb-1 px-3 text-secondary">
              {category.name}
            </Typography>
            {/* Rows carry no divider, so this gap is what separates them. */}
            <div className="flex flex-col gap-1">
              {category.apps.map((app) => (
                <AppRow key={app.id} app={app} onClick={() => onAppClick(app)} />
              ))}
            </div>
          </div>
        ))}
        {hasNoMatches && (
          <p
            className="py-8 text-center text-s-400 text-secondary"
            data-testid="text-no-apps-found"
          >
            {searchTerm
              ? `No apps or tools match \u201C${searchTerm}\u201D.`
              : 'Nothing in the selected categories yet.'}
          </p>
        )}
      </div>
    </ScrollArea>
  )
}
